import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from '../../node_modules/@flixbus/honeycomb/dist/scss/components/calendar.scss.js';
import { calendarFactory } from './calendar-factory/calendar-factory.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import '@flixbus/honeycomb-icons-react';
import '../../packages/props-utils/prop-types.js';
import { isSameMonth } from './calendar-utils.js';
import { CalendarBody } from './quarks/calendar-body/calendar-body.js';
import { CalendarHeading } from './quarks/calendar-heading/calendar-heading.js';
import { HeaderBirthday } from './quarks/header-birthday/header-birthday.js';
import { HeaderCompact } from './quarks/header-compact/header-compact.js';
import { CALENDAR_APPEARANCES } from './types.js';
import { Injecss } from '../../packages/injecss/injecss.js';

const bem = new HoneyBem('calendar', styles);
/** @since 1.3.0 */
function Calendar(_ref) {
  let {
    months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    days = [{
      short: 'Mo',
      long: 'Monday'
    }, {
      short: 'Tu',
      long: 'Tuesday'
    }, {
      short: 'We',
      long: 'Wednesday'
    }, {
      short: 'Th',
      long: 'Thursday'
    }, {
      short: 'Fr',
      long: 'Friday'
    }, {
      short: 'Sa',
      long: 'Saturday'
    }, {
      short: 'Su',
      long: 'Sunday'
    }],
    firstDayOfWeek = 1,
    selected,
    showCurrentDate = false,
    startSelected,
    defaultMonth = new Date(),
    endSelected,
    startDate = new Date(),
    endDate = new Date(2019, 10, 0),
    handleSelect = () => null,
    flat = false,
    horizontal = false,
    appearance = 'list',
    hidden = false,
    compactPrevButton = {
      'aria-label': 'Previous month'
    },
    compactNextButton = {
      'aria-label': 'Next month'
    },
    compactSelect = {
      'aria-label': 'Select month and year'
    },
    birthdaySelectMonth = {
      'aria-label': 'Select month'
    },
    birthdaySelectYear = {
      'aria-label': 'Select year'
    },
    dayLabel = (day, month, year) => "".concat(new Date(year, month, day).toLocaleDateString()),
    disableDayCondition,
    testCalendar,
    id,
    extraClasses
  } = _ref;
  // all required states
  const [calendar, setCalendar] = useState(testCalendar);
  const [displayedMonth, setDisplayedMonth] = useState(defaultMonth);
  const [displayedCalendar, setDisplayedCalendar] = useState(testCalendar);
  const [hoveredEndDate, setHoveredEndDate] = useState(endSelected);
  // if calendar changes recompute displayedCalendar
  useEffect(() => {
    setDisplayedCalendar(calendar !== undefined && appearance !== 'list' && displayedMonth ? Object.assign(Object.assign({}, calendar), {
      months: [calendar.months.find(_ref2 => {
        let {
          year,
          month
        } = _ref2;
        return isSameMonth(displayedMonth, new Date(year, month, 1));
      }) || calendar.months[0]]
    }) : calendar);
  }, [calendar, displayedMonth]);
  // updating the calendar when changing the defaultMonth
  useEffect(() => {
    // we won't set month if the value provided is outside of start/end date range
    if (defaultMonth > startDate && defaultMonth < endDate) {
      setDisplayedMonth(defaultMonth);
    }
  }, [
  // we only need to recalculate this once the day changes
  defaultMonth.getDate(), defaultMonth.getMonth(), defaultMonth.getFullYear(), startDate.getDate(), startDate.getMonth(), startDate.getFullYear(), endDate.getDate(), endDate.getMonth(), endDate.getFullYear()]);
  // is start/end/weekStart change, recalculate calendar
  useEffect(() => {
    setCalendar(calendarFactory(startDate, endDate, {
      offset: firstDayOfWeek,
      disableDayFn: disableDayCondition
    }));
  }, [startDate.getDate(), startDate.getMonth(), startDate.getFullYear(), endDate.getDate(), endDate.getMonth(), endDate.getFullYear(), firstDayOfWeek, disableDayCondition]);
  // Prev month handler
  const onPrev = () => {
    setDisplayedMonth(new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() - 1, 1));
  };
  // Next month handler
  const onNext = () => {
    setDisplayedMonth(new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 1));
  };
  // Change date handler
  const onChangeDate = date => setDisplayedMonth(date);
  const isMultiselect = startSelected !== undefined || endSelected !== undefined ? {
    'aria-multiselectable': true
  } : {};
  const isLive = appearance !== 'list' ? {
    'aria-live': 'polite'
  } : {};
  return /*#__PURE__*/React.createElement("div", {
    className: bem.b({
      flat,
      horizontal
    }, extraClasses),
    hidden: hidden
  }, displayedCalendar !== undefined && displayedCalendar.months.map(_ref3 => {
    let {
      weeks,
      month,
      year
    } = _ref3;
    return /*#__PURE__*/React.createElement("div", {
      className: bem.e('month'),
      key: "".concat(year, "-").concat(month)
    }, appearance === 'compact' && (/*#__PURE__*/React.createElement(HeaderCompact, {
      hasPrev: isSameMonth(displayedMonth, startDate),
      onPrev: onPrev,
      hasNext: isSameMonth(displayedMonth, endDate),
      onNext: onNext,
      onChangeDate: onChangeDate,
      selected: displayedMonth,
      calendar: calendar,
      months: months,
      compactPrevButton: compactPrevButton,
      compactNextButton: compactNextButton,
      compactSelect: compactSelect,
      id: id
    })), appearance === 'birthday' && (/*#__PURE__*/React.createElement(HeaderBirthday, {
      selected: displayedMonth,
      calendar: calendar,
      onChangeDate: onChangeDate,
      months: months,
      birthdaySelectMonth: birthdaySelectMonth,
      birthdaySelectYear: birthdaySelectYear,
      id: id
    })), /*#__PURE__*/React.createElement("table", Object.assign({
      id: "".concat(id, "-grid"),
      role: "grid",
      className: bem.e('table')
    }, isLive, isMultiselect), appearance === 'list' && (/*#__PURE__*/React.createElement("caption", {
      className: bem.e('caption')
    }, "".concat(months[month], " ").concat(year))), /*#__PURE__*/React.createElement(CalendarHeading, {
      days: days
    }), /*#__PURE__*/React.createElement(CalendarBody, {
      weeks: weeks,
      month: month,
      year: year,
      startSelected: startSelected,
      endSelected: endSelected,
      selected: selected,
      hoveredEndDate: hoveredEndDate,
      setHoveredEndDate: setHoveredEndDate,
      setDisplayedMonth: setDisplayedMonth,
      handleSelect: handleSelect,
      dayLabel: dayLabel,
      currentDate: showCurrentDate ? new Date() : undefined
    })));
  }));
}
Calendar.displayName = 'Calendar';
Calendar.propTypes = {
  months: PropTypes.arrayOf(PropTypes.string),
  days: PropTypes.arrayOf(PropTypes.shape({
    short: PropTypes.string.isRequired,
    long: PropTypes.string.isRequired
  })),
  firstDayOfWeek: PropTypes.number,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  selected: PropTypes.instanceOf(Date),
  startSelected: PropTypes.instanceOf(Date),
  endSelected: PropTypes.instanceOf(Date),
  handleSelect: PropTypes.func,
  flat: PropTypes.bool,
  appearance: PropTypes.oneOf(CALENDAR_APPEARANCES),
  defaultMonth: PropTypes.instanceOf(Date),
  hidden: PropTypes.bool,
  compactPrevButton: PropTypes.shape({}),
  compactNextButton: PropTypes.shape({}),
  compactSelect: PropTypes.shape({}),
  birthdaySelectMonth: PropTypes.shape({}),
  birthdaySelectYear: PropTypes.shape({}),
  dayLabel: PropTypes.func,
  extraClasses: PropTypes.string,
  testCalendar: PropTypes.shape({}),
  id: PropTypes.string.isRequired,
  horizontal: PropTypes.bool,
  showCurrentDate: PropTypes.bool,
  disableDayCondition: PropTypes.func
};
var calendar = Injecss(Calendar, styles);

export { Calendar, calendar as default };
